import { useEffect, useState, useCallback, useReducer } from "react";
import {
  Show,
  Datagrid,
  FunctionField,
  ListContextProvider,
  Pagination,
  Record,
} from "react-admin";
import { EditActions } from "../../common/form/FormActions";
import { GenericCompactDateOrTimePicker } from "../../common/form/GenericCompactDateOrTimePicker";
import { DatePicker } from "@material-ui/pickers";
import { addMonths, addDays } from "date-fns";
import { useParams, useLocation } from "react-router-dom";
import {
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  useTheme,
} from "@material-ui/core";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import {
  MasterEldDeviceUsageApiFactory,
  EldDeviceUsageByDaysDtoPagedResultDto,
  TenantRegistrationDto,
  EldDeviceUsageByDaysDto,
} from "../../../common/data/service-proxies";
import styles from "./TenantEldDevicesUsage.module.css";

interface RouteParams {
  vehicleId: string;
  serialNumber: string;
}

interface Props {}

interface ViewProps {
  record?: Record;
}

type Action = "SET_DATEFROM" | "SET_DATETO";

interface IFilter {
  dateFrom: Date;
  dateTo: Date;
}

interface IAction {
  type: Action;
  payload: Date;
}

const reducer = (prev: IFilter, action: IAction) => {
  if (!action) {
    return prev;
  }

  switch (action.type) {
    case "SET_DATEFROM":
      return { ...prev, dateFrom: action.payload };
    case "SET_DATETO":
      return { ...prev, dateTo: action.payload };
  }
  return prev;
};

export const TenantEldUsageByEventsByDays = (props: Props) => {
  const params = useParams<RouteParams>();

  return (
    <Show
      {...props}
      title={`View ELD usage by days ${params.serialNumber || ""}`}
      actions={<EditActions />}
      component="div"
    >
      <TenantEldUsageByEventsByDaysView />
    </Show>
  );
};

export const TenantEldUsageByEventsByDaysView = (
  props: ViewProps
): JSX.Element => {
  const params = useParams<RouteParams>();
  const location = useLocation();
  const axios = useAxiosInstance();
  const theme = useTheme();
  const today = new Date();
  const previousMonth = addMonths(today, -1);
  const [filter, dispatch] = useReducer(reducer, {
    dateFrom: new Date(
      previousMonth.getFullYear(),
      previousMonth.getMonth(),
      1
    ),
    dateTo: addDays(new Date(today.getFullYear(), today.getMonth(), 1), -1),
  });

  const [data, setData] = useState<EldDeviceUsageByDaysDtoPagedResultDto>();
  const [loading, setLoading] = useState<boolean>(false);
  const [sortField, setSortField] = useState<string>("date");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(25);

  const record = props.record as TenantRegistrationDto;

  const searchParams = new URLSearchParams(location.search);
  const vehicleName = searchParams.get("vehicleName") || "Unknown Vehicle";

  const handleSort = useCallback((field: string, order?: string) => {
    const newSortOrder = order === "DESC" ? "DESC" : "ASC";
    setSortField(field);
    setSortOrder(newSortOrder);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePerPageChange = useCallback((newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  }, []);

  useEffect(() => {
    if (!axios || !record?.createdTenantId) {
      return;
    }

    setLoading(true);

    const filterRules = [];

    filterRules.push({
      field: "dateFrom",
      operator: "equals",
      value: filter.dateFrom.toISOString(),
    });

    filterRules.push({
      field: "dateTo",
      operator: "equals",
      value: filter.dateTo.toISOString(),
    });

    filterRules.push({
      field: "vehicleId",
      operator: "equals",
      value: params.vehicleId,
    });

    filterRules.push({
      field: "tenantId",
      operator: "equals",
      value: record.createdTenantId,
    });

    filterRules.push({
      field: "serialNumber",
      operator: "equals",
      value: params.serialNumber,
    });

    const filterRule = {
      condition: "and",
      filterRules: filterRules,
    };

    MasterEldDeviceUsageApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppMasterelddeviceusageGetEldDeviceUsageByDaysPost({
        filterRule,
        skipCount: (page - 1) * perPage,
        maxResultCount: perPage,
        sorting: `${sortField} ${sortOrder.toLowerCase()}`,
      })
      .then((response) => {
        setData(response.data);
      })
      .catch((error) => {
        console.error("❌ API ERROR:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    axios,
    filter,
    params.vehicleId,
    params.serialNumber,
    record?.createdTenantId,
    sortField,
    sortOrder,
    page,
    perPage,
  ]);

  const currentSort = { field: sortField, order: sortOrder };

  if (!params.vehicleId || !params.serialNumber || !record?.createdTenantId) {
    return <div>Missing required parameters</div>;
  }

  return (
    <Card>
      <CardContent>
        <div className={styles.header}>
          <Typography variant="h6">ELD Usage by Days</Typography>
          <div className={styles.filters}>
            <GenericCompactDateOrTimePicker
              variant="outlined"
              PickerComponent={DatePicker}
              source="dateFrom"
              label="Date from"
              value={filter.dateFrom}
              onChange={(value) =>
                dispatch({ type: "SET_DATEFROM", payload: value })
              }
            />
            <GenericCompactDateOrTimePicker
              variant="outlined"
              PickerComponent={DatePicker}
              source="dateTo"
              label="Date to"
              value={filter.dateTo}
              onChange={(value) =>
                dispatch({ type: "SET_DATETO", payload: value })
              }
            />
          </div>
        </div>

        <div
          style={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "8px",
            padding: "16px",
            marginTop: "16px",
            marginBottom: "20px",
            backgroundColor: "transparent",
          }}
        >
          <div>
            Displayed below is the daily usage for the ELD device with serial
            number{" "}
            <span
              style={{
                fontWeight: "bold",
                color: theme.palette.primary.main,
              }}
            >
              {params.serialNumber}
            </span>
            , assigned to vehicle{" "}
            <span
              style={{
                fontWeight: "bold",
                color: theme.palette.primary.main,
              }}
            >
              {vehicleName}
            </span>
            .
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <CircularProgress />
            <Typography variant="body2" style={{ marginTop: "16px" }}>
              Loading ELD usage by days data...
            </Typography>
          </div>
        ) : (
          <div style={{ marginTop: "20px" }}>
            {data?.items && data.items.length > 0 ? (
              <ListContextProvider
                value={{
                  data: data.items.reduce(
                    (
                      acc: { [key: string]: EldDeviceUsageByDaysDto },
                      item: EldDeviceUsageByDaysDto,
                      index: number
                    ) => {
                      acc[item.date || index] = item;
                      return acc;
                    },
                    {}
                  ),
                  ids: data.items.map(
                    (item: EldDeviceUsageByDaysDto, index: number) =>
                      item.date || index
                  ),
                  total: data.totalCount || data.items.length,
                  page,
                  perPage,
                  currentSort,
                  setSort: handleSort,
                  setPage: handlePageChange,
                  setPerPage: handlePerPageChange,
                  resource: "eld-usage-by-events-by-days",
                  basePath: "/eld-usage-by-events-by-days",
                }}
              >
                <Datagrid hasBulkActions={false} optimized>
                  <FunctionField
                    source="date"
                    label="Date"
                    sortable
                    render={(record?: Record) =>
                      record?.date
                        ? new Date(record.date).toLocaleDateString()
                        : "-"
                    }
                  />
                </Datagrid>
                <Pagination />
              </ListContextProvider>
            ) : (
              <Paper style={{ padding: "40px", textAlign: "center" }}>
                <Typography variant="body1" color="textSecondary">
                  No ELD usage data found for the selected date range.
                </Typography>
              </Paper>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
